/**
 * Simple in-app i18n system — no external library.
 *
 * Structure: `TRANSLATIONS[lang][key] = string`. Missing keys fall back
 * to French, then to the key itself so a missing translation still
 * renders something readable.
 *
 * The `t(key, params)` helper supports `{placeholder}` interpolation.
 *
 * Supported languages: fr (default), en, it, es.
 */

export const SUPPORTED_LANGS = [
  { code: "fr", label: "Français", flag: "🇫🇷" },
  { code: "en", label: "English", flag: "🇬🇧" },
  { code: "it", label: "Italiano", flag: "🇮🇹" },
  { code: "es", label: "Español", flag: "🇪🇸" },
];

// eslint-disable-next-line
const TRANSLATIONS = {
  fr: {
    // ── Registration ────────────────────────────────────────────
    "register.title": "Créer mon compte",
    "register.subtitle": "Réservez votre prochaine course Saor en toute discrétion.",
    "register.full_name": "Nom complet",
    "register.full_name_placeholder": "Jean Dupont",
    "register.email": "Email",
    "register.email_placeholder": "jean.dupont@email.com",
    "register.phone": "Téléphone",
    "register.phone_placeholder": "+33 6 12 34 56 78",
    "register.password": "Mot de passe",
    "register.password_placeholder": "8 caractères minimum",
    "register.password_confirm": "Confirmer le mot de passe",
    "register.password_confirm_placeholder": "Retapez votre mot de passe",
    "register.referral_code": "Code parrainage",
    "register.referral_optional": "(optionnel)",
    "register.language": "Langue",
    "register.submit": "Créer mon compte",
    "register.already_registered": "Déjà inscrit ?",
    "register.login": "Se connecter",
    "register.err_name_required": "Nom complet requis",
    "register.err_email_required": "Email requis",
    "register.err_phone_required": "Téléphone requis",
    "register.err_password_min": "Mot de passe : minimum 8 caractères",
    "register.err_password_mismatch": "Les mots de passe ne correspondent pas",
    "register.success": "Compte créé — bienvenue sur Saor !",
    "register.success_with_bonus": "Compte créé — bienvenue ! 10 € offerts sur ta 1ʳᵉ course.",
    "register.err_generic": "Erreur lors de l'inscription",
    "register.ref_valid": "Parrainé par {name} — 10 € offerts à ta 1ʳᵉ course",
    "register.ref_valid_driver": "Parrainé par ton chauffeur {name} — 10 € offerts à ta 1ʳᵉ course",
    "register.ref_invalid": "Code introuvable",
    "register.ref_checking": "Vérification…",

    // ── Dashboard : statuts course ────────────────────────────────
    "status.pending": "Recherche d'un chauffeur…",
    "status.accepted": "Chauffeur en approche",
    "status.picked_up": "Chauffeur arrivé",
    "status.in_progress": "Trajet en cours",
    "status.completed": "Course terminée",
    "status.cancelled": "Course annulée",

    // ── Dashboard : actions ───────────────────────────────────────
    "action.cancel_ride": "Annuler la course",
    "action.book_ride": "Réserver",
    "action.call_driver": "Appeler",
    "action.chat_driver": "Chat",
    "action.help": "Aide",
    "action.logout": "Déconnexion",
    "action.referral": "Parrainage",
    "action.language": "Langue",

    // ── Dashboard : cartes info ───────────────────────────────────
    "card.driver_label": "Chauffeur",
    "card.eta_approach": "Chauffeur en approche",
    "card.eta_arrived": "Chauffeur arrivé",
    "card.eta_destination": "ETA destination",

    // ── WhatsApp CTA ──────────────────────────────────────────────
    "wa.need_help": "Besoin d'aide ?",
    "wa.cta_title": "Écris-nous sur WhatsApp pour une prise en charge encore plus rapide",
    "wa.pre_message": "Bonjour, j'attends un chauffeur Saor depuis quelques minutes. Course #{code}",

    // ── Booking form ──────────────────────────────────────────────
    "form.pickup": "Adresse de départ",
    "form.destination": "Destination",
    "form.passengers": "Passagers",
    "form.luggage": "Bagages",
    "form.vehicle_type": "Type de véhicule",
    "form.payment_method": "Paiement",
    "form.pay_cash": "Espèces",
    "form.pay_card": "Carte",
    "form.notes": "Notes (optionnel)",
    "form.submit": "Commander ma course",
    "form.estimating": "Estimation en cours…",
    "form.choose_vehicle": "Choisir un véhicule",
    "form.when": "Quand",
    "form.when_scheduled": "Prog.",
    "form.when_immediate": "Immédiat",
    "form.animal": "Animal",
    "form.pmr": "PMR",
    "form.yes": "Oui",
    "form.no": "Non",
    "form.instructions": "Instructions spéciales",
    "form.instructions_placeholder": "Siège enfant, bagage fragile, arrêt intermédiaire…",
    "form.estimation": "Estimation",
    "form.fixed_zone_prefix": "Forfait",
    "form.event_prefix": "Événement",
    "form.km_at_price": "≈ {km} km · au tarif kilométrique",
    "form.referral_credit_applied": "Crédit parrainage appliqué",
    "form.my_position": "Ma position",
    "form.book_immediate": "Demander un chauffeur",
    "form.book_scheduled": "Programmer la course",
    "form.payment_footer": "Paiement {method} ou à bord · Chauffeurs certifiés Saor",
    "form.err_pickup_required": "Adresse de départ requise",
    "form.err_dest_required": "Destination requise",
    "form.err_price_wait": "Sélectionne une adresse dans la liste et attends le prix (2 s)",
    "pay.title": "Comment souhaitez-vous payer ?",
    "pay.total": "Total à régler",
    "pay.referral_line": "Crédit parrainage : − {amount} €",
    "pay.saved_default": "Carte par défaut · 1 tap",
    "pay.saved_other": "Payer avec cette carte",
    "pay.cards_loading": "Chargement de vos cartes…",
    "pay.immediate": "Paiement immédiat",
    "pay.on_board": "Paiement à bord",
    "pay.on_board_sub": "Espèces ou carte chez le chauffeur",
    "pay.footer": "Paiement sécurisé · Chauffeurs certifiés Saor",
    "pay.in_progress_title": "Paiement en cours",
    "pay.in_progress_body": "Validez sur Safari. La course démarrera automatiquement dès validation.",
    "pay.cancel_pending": "Annuler la demande de course ?",
    "pay.cancel_action": "Annuler",
    "pay.cancelled": "Course annulée",

    // ── Push notification titles/bodies (server-side lookup uses same keys) ──
    "push.driver_accepted.title": "Chauffeur assigné",
    "push.driver_accepted.body": "Votre chauffeur arrive dans {eta} min.",
    "push.driver_arrived.title": "Votre chauffeur est arrivé",
    "push.driver_arrived.body": "{driver_name} vous attend au point de rendez-vous.",
    "push.ride_started.title": "Trajet démarré",
    "push.ride_started.body": "Bonne route avec Saor.",
    "push.ride_completed.title": "Course terminée",
    "push.ride_completed.body": "Merci d'avoir voyagé avec Saor.",
  },
  en: {
    "register.title": "Create my account",
    "register.subtitle": "Book your next Saor ride discreetly.",
    "register.full_name": "Full name",
    "register.full_name_placeholder": "John Smith",
    "register.email": "Email",
    "register.email_placeholder": "john.smith@email.com",
    "register.phone": "Phone",
    "register.phone_placeholder": "+44 7911 123456",
    "register.password": "Password",
    "register.password_placeholder": "Minimum 8 characters",
    "register.password_confirm": "Confirm password",
    "register.password_confirm_placeholder": "Retype your password",
    "register.referral_code": "Referral code",
    "register.referral_optional": "(optional)",
    "register.language": "Language",
    "register.submit": "Create my account",
    "register.already_registered": "Already registered?",
    "register.login": "Sign in",
    "register.err_name_required": "Full name required",
    "register.err_email_required": "Email required",
    "register.err_phone_required": "Phone required",
    "register.err_password_min": "Password: 8 characters minimum",
    "register.err_password_mismatch": "Passwords do not match",
    "register.success": "Account created — welcome to Saor!",
    "register.success_with_bonus": "Account created — welcome! €10 off your 1st ride.",
    "register.err_generic": "Registration error",
    "register.ref_valid": "Referred by {name} — €10 off your 1st ride",
    "register.ref_valid_driver": "Referred by your driver {name} — €10 off your 1st ride",
    "register.ref_invalid": "Code not found",
    "register.ref_checking": "Checking…",

    "status.pending": "Looking for a driver…",
    "status.accepted": "Driver on the way",
    "status.picked_up": "Driver arrived",
    "status.in_progress": "Ride in progress",
    "status.completed": "Ride completed",
    "status.cancelled": "Ride cancelled",

    "action.cancel_ride": "Cancel ride",
    "action.book_ride": "Book",
    "action.call_driver": "Call",
    "action.chat_driver": "Chat",
    "action.help": "Help",
    "action.logout": "Sign out",
    "action.referral": "Referral",
    "action.language": "Language",

    "card.driver_label": "Driver",
    "card.eta_approach": "Driver on the way",
    "card.eta_arrived": "Driver arrived",
    "card.eta_destination": "ETA destination",

    "wa.need_help": "Need help?",
    "wa.cta_title": "Message us on WhatsApp for even faster service",
    "wa.pre_message": "Hi, I've been waiting for a Saor driver for a few minutes. Ride #{code}",

    "form.pickup": "Pickup address",
    "form.destination": "Destination",
    "form.passengers": "Passengers",
    "form.luggage": "Luggage",
    "form.vehicle_type": "Vehicle type",
    "form.payment_method": "Payment",
    "form.pay_cash": "Cash",
    "form.pay_card": "Card",
    "form.notes": "Notes (optional)",
    "form.submit": "Book my ride",
    "form.estimating": "Estimating…",
    "form.choose_vehicle": "Choose a vehicle",
    "form.when": "When",
    "form.when_scheduled": "Sched.",
    "form.when_immediate": "Now",
    "form.animal": "Pet",
    "form.pmr": "PRM",
    "form.yes": "Yes",
    "form.no": "No",
    "form.instructions": "Special instructions",
    "form.instructions_placeholder": "Child seat, fragile luggage, extra stop…",
    "form.estimation": "Estimate",
    "form.fixed_zone_prefix": "Flat rate",
    "form.event_prefix": "Event",
    "form.km_at_price": "≈ {km} km · per-km fare",
    "form.referral_credit_applied": "Referral credit applied",
    "form.my_position": "My location",
    "form.book_immediate": "Request a driver",
    "form.book_scheduled": "Schedule this ride",
    "form.payment_footer": "Pay with {method} or on board · Certified Saor drivers",
    "form.err_pickup_required": "Pickup address required",
    "form.err_dest_required": "Destination required",
    "form.err_price_wait": "Pick an address from the list and wait for the price (2 s)",
    "pay.title": "How would you like to pay?",
    "pay.total": "Total to pay",
    "pay.referral_line": "Referral credit: − {amount} €",
    "pay.saved_default": "Default card · 1 tap",
    "pay.saved_other": "Pay with this card",
    "pay.cards_loading": "Loading your cards…",
    "pay.immediate": "Instant payment",
    "pay.on_board": "Pay on board",
    "pay.on_board_sub": "Cash or card with the driver",
    "pay.footer": "Secure payment · Certified Saor drivers",
    "pay.in_progress_title": "Payment in progress",
    "pay.in_progress_body": "Confirm in Safari. Your ride starts as soon as it's approved.",
    "pay.cancel_pending": "Cancel this ride request?",
    "pay.cancel_action": "Cancel",
    "pay.cancelled": "Ride cancelled",

    "push.driver_accepted.title": "Driver assigned",
    "push.driver_accepted.body": "Your driver arrives in {eta} min.",
    "push.driver_arrived.title": "Your driver has arrived",
    "push.driver_arrived.body": "{driver_name} is waiting at the pickup point.",
    "push.ride_started.title": "Ride started",
    "push.ride_started.body": "Enjoy your ride with Saor.",
    "push.ride_completed.title": "Ride completed",
    "push.ride_completed.body": "Thank you for traveling with Saor.",
  },
  it: {
    "register.title": "Crea il mio account",
    "register.subtitle": "Prenota la tua prossima corsa Saor in tutta discrezione.",
    "register.full_name": "Nome completo",
    "register.full_name_placeholder": "Mario Rossi",
    "register.email": "Email",
    "register.email_placeholder": "mario.rossi@email.com",
    "register.phone": "Telefono",
    "register.phone_placeholder": "+39 333 123 4567",
    "register.password": "Password",
    "register.password_placeholder": "Almeno 8 caratteri",
    "register.password_confirm": "Conferma password",
    "register.password_confirm_placeholder": "Ripeti la password",
    "register.referral_code": "Codice invito",
    "register.referral_optional": "(opzionale)",
    "register.language": "Lingua",
    "register.submit": "Crea il mio account",
    "register.already_registered": "Già registrato?",
    "register.login": "Accedi",
    "register.err_name_required": "Nome completo richiesto",
    "register.err_email_required": "Email richiesta",
    "register.err_phone_required": "Telefono richiesto",
    "register.err_password_min": "Password: minimo 8 caratteri",
    "register.err_password_mismatch": "Le password non corrispondono",
    "register.success": "Account creato — benvenuto in Saor!",
    "register.success_with_bonus": "Account creato — benvenuto! 10 € sulla tua 1ª corsa.",
    "register.err_generic": "Errore durante la registrazione",
    "register.ref_valid": "Invitato da {name} — 10 € sulla tua 1ª corsa",
    "register.ref_valid_driver": "Invitato dal tuo autista {name} — 10 € sulla tua 1ª corsa",
    "register.ref_invalid": "Codice non trovato",
    "register.ref_checking": "Verifica…",

    "status.pending": "Ricerca di un autista…",
    "status.accepted": "Autista in arrivo",
    "status.picked_up": "Autista arrivato",
    "status.in_progress": "Corsa in corso",
    "status.completed": "Corsa terminata",
    "status.cancelled": "Corsa annullata",

    "action.cancel_ride": "Annulla corsa",
    "action.book_ride": "Prenota",
    "action.call_driver": "Chiama",
    "action.chat_driver": "Chat",
    "action.help": "Aiuto",
    "action.logout": "Esci",
    "action.referral": "Invita",
    "action.language": "Lingua",

    "card.driver_label": "Autista",
    "card.eta_approach": "Autista in arrivo",
    "card.eta_arrived": "Autista arrivato",
    "card.eta_destination": "ETA destinazione",

    "wa.need_help": "Ti serve aiuto?",
    "wa.cta_title": "Scrivici su WhatsApp per un servizio ancora più rapido",
    "wa.pre_message": "Ciao, aspetto un autista Saor da qualche minuto. Corsa #{code}",

    "form.pickup": "Indirizzo di partenza",
    "form.destination": "Destinazione",
    "form.passengers": "Passeggeri",
    "form.luggage": "Bagagli",
    "form.vehicle_type": "Tipo di veicolo",
    "form.payment_method": "Pagamento",
    "form.pay_cash": "Contanti",
    "form.pay_card": "Carta",
    "form.notes": "Note (opzionale)",
    "form.submit": "Prenota la corsa",
    "form.estimating": "Stima in corso…",
    "form.choose_vehicle": "Scegli un veicolo",
    "form.when": "Quando",
    "form.when_scheduled": "Prog.",
    "form.when_immediate": "Subito",
    "form.animal": "Animale",
    "form.pmr": "Disab.",
    "form.yes": "Sì",
    "form.no": "No",
    "form.instructions": "Istruzioni speciali",
    "form.instructions_placeholder": "Seggiolino, bagaglio fragile, tappa intermedia…",
    "form.estimation": "Stima",
    "form.fixed_zone_prefix": "Forfait",
    "form.event_prefix": "Evento",
    "form.km_at_price": "≈ {km} km · tariffa al km",
    "form.referral_credit_applied": "Credito invito applicato",
    "form.my_position": "La mia posizione",
    "form.book_immediate": "Richiedi un autista",
    "form.book_scheduled": "Programma la corsa",
    "form.payment_footer": "Pagamento {method} o a bordo · Autisti certificati Saor",
    "form.err_pickup_required": "Indirizzo di partenza richiesto",
    "form.err_dest_required": "Destinazione richiesta",
    "form.err_price_wait": "Seleziona un indirizzo dalla lista e attendi il prezzo (2 s)",
    "pay.title": "Come vuoi pagare?",
    "pay.total": "Totale da pagare",
    "pay.referral_line": "Credito invito: − {amount} €",
    "pay.saved_default": "Carta predefinita · 1 tap",
    "pay.saved_other": "Paga con questa carta",
    "pay.cards_loading": "Caricamento delle tue carte…",
    "pay.immediate": "Pagamento immediato",
    "pay.on_board": "Pagamento a bordo",
    "pay.on_board_sub": "Contanti o carta dall'autista",
    "pay.footer": "Pagamento sicuro · Autisti certificati Saor",
    "pay.in_progress_title": "Pagamento in corso",
    "pay.in_progress_body": "Conferma in Safari. La corsa parte appena approvato.",
    "pay.cancel_pending": "Annullare la richiesta di corsa?",
    "pay.cancel_action": "Annulla",
    "pay.cancelled": "Corsa annullata",

    "push.driver_accepted.title": "Autista assegnato",
    "push.driver_accepted.body": "Il tuo autista arriva tra {eta} min.",
    "push.driver_arrived.title": "Il tuo autista è arrivato",
    "push.driver_arrived.body": "{driver_name} ti aspetta al punto di ritrovo.",
    "push.ride_started.title": "Corsa iniziata",
    "push.ride_started.body": "Buon viaggio con Saor.",
    "push.ride_completed.title": "Corsa terminata",
    "push.ride_completed.body": "Grazie di aver viaggiato con Saor.",
  },
  es: {
    "register.title": "Crear mi cuenta",
    "register.subtitle": "Reserva tu próximo viaje Saor con total discreción.",
    "register.full_name": "Nombre completo",
    "register.full_name_placeholder": "Juan García",
    "register.email": "Correo",
    "register.email_placeholder": "juan.garcia@email.com",
    "register.phone": "Teléfono",
    "register.phone_placeholder": "+34 612 34 56 78",
    "register.password": "Contraseña",
    "register.password_placeholder": "Mínimo 8 caracteres",
    "register.password_confirm": "Confirmar contraseña",
    "register.password_confirm_placeholder": "Reescribe tu contraseña",
    "register.referral_code": "Código de invitación",
    "register.referral_optional": "(opcional)",
    "register.language": "Idioma",
    "register.submit": "Crear mi cuenta",
    "register.already_registered": "¿Ya registrado?",
    "register.login": "Iniciar sesión",
    "register.err_name_required": "Nombre completo requerido",
    "register.err_email_required": "Correo requerido",
    "register.err_phone_required": "Teléfono requerido",
    "register.err_password_min": "Contraseña: mínimo 8 caracteres",
    "register.err_password_mismatch": "Las contraseñas no coinciden",
    "register.success": "¡Cuenta creada — bienvenido a Saor!",
    "register.success_with_bonus": "¡Cuenta creada — bienvenido! 10 € en tu 1er viaje.",
    "register.err_generic": "Error durante el registro",
    "register.ref_valid": "Invitado por {name} — 10 € en tu 1er viaje",
    "register.ref_valid_driver": "Invitado por tu chófer {name} — 10 € en tu 1er viaje",
    "register.ref_invalid": "Código no encontrado",
    "register.ref_checking": "Verificando…",

    "status.pending": "Buscando un chófer…",
    "status.accepted": "Chófer en camino",
    "status.picked_up": "Chófer llegado",
    "status.in_progress": "Viaje en curso",
    "status.completed": "Viaje terminado",
    "status.cancelled": "Viaje cancelado",

    "action.cancel_ride": "Cancelar viaje",
    "action.book_ride": "Reservar",
    "action.call_driver": "Llamar",
    "action.chat_driver": "Chat",
    "action.help": "Ayuda",
    "action.logout": "Cerrar sesión",
    "action.referral": "Invitar",
    "action.language": "Idioma",

    "card.driver_label": "Chófer",
    "card.eta_approach": "Chófer en camino",
    "card.eta_arrived": "Chófer llegado",
    "card.eta_destination": "ETA destino",

    "wa.need_help": "¿Necesitas ayuda?",
    "wa.cta_title": "Escríbenos por WhatsApp para un servicio aún más rápido",
    "wa.pre_message": "Hola, llevo unos minutos esperando un chófer Saor. Viaje #{code}",

    "form.pickup": "Dirección de recogida",
    "form.destination": "Destino",
    "form.passengers": "Pasajeros",
    "form.luggage": "Equipaje",
    "form.vehicle_type": "Tipo de vehículo",
    "form.payment_method": "Pago",
    "form.pay_cash": "Efectivo",
    "form.pay_card": "Tarjeta",
    "form.notes": "Notas (opcional)",
    "form.submit": "Reservar mi viaje",
    "form.estimating": "Calculando…",
    "form.choose_vehicle": "Elige un vehículo",
    "form.when": "Cuándo",
    "form.when_scheduled": "Prog.",
    "form.when_immediate": "Ahora",
    "form.animal": "Mascota",
    "form.pmr": "PMR",
    "form.yes": "Sí",
    "form.no": "No",
    "form.instructions": "Instrucciones especiales",
    "form.instructions_placeholder": "Silla infantil, equipaje frágil, parada intermedia…",
    "form.estimation": "Estimación",
    "form.fixed_zone_prefix": "Precio fijo",
    "form.event_prefix": "Evento",
    "form.km_at_price": "≈ {km} km · tarifa por km",
    "form.referral_credit_applied": "Crédito de invitación aplicado",
    "form.my_position": "Mi ubicación",
    "form.book_immediate": "Solicitar un chófer",
    "form.book_scheduled": "Programar el viaje",
    "form.payment_footer": "Pago con {method} o a bordo · Chóferes certificados Saor",
    "form.err_pickup_required": "Dirección de recogida requerida",
    "form.err_dest_required": "Destino requerido",
    "form.err_price_wait": "Elige una dirección de la lista y espera el precio (2 s)",
    "pay.title": "¿Cómo quieres pagar?",
    "pay.total": "Total a pagar",
    "pay.referral_line": "Crédito de invitación: − {amount} €",
    "pay.saved_default": "Tarjeta predeterminada · 1 tap",
    "pay.saved_other": "Pagar con esta tarjeta",
    "pay.cards_loading": "Cargando tus tarjetas…",
    "pay.immediate": "Pago inmediato",
    "pay.on_board": "Pago a bordo",
    "pay.on_board_sub": "Efectivo o tarjeta con el chófer",
    "pay.footer": "Pago seguro · Chóferes certificados Saor",
    "pay.in_progress_title": "Pago en curso",
    "pay.in_progress_body": "Confirma en Safari. El viaje empezará al aprobarse.",
    "pay.cancel_pending": "¿Cancelar la solicitud de viaje?",
    "pay.cancel_action": "Cancelar",
    "pay.cancelled": "Viaje cancelado",

    "push.driver_accepted.title": "Chófer asignado",
    "push.driver_accepted.body": "Tu chófer llega en {eta} min.",
    "push.driver_arrived.title": "Tu chófer ha llegado",
    "push.driver_arrived.body": "{driver_name} te espera en el punto de recogida.",
    "push.ride_started.title": "Viaje iniciado",
    "push.ride_started.body": "Buen viaje con Saor.",
    "push.ride_completed.title": "Viaje terminado",
    "push.ride_completed.body": "Gracias por viajar con Saor.",
  },
};

/** Return the translation for `key` in `lang`, falling back to FR then to
 * the key itself. Supports {placeholder} interpolation via `params`. */
export function translate(lang, key, params) {
  const bag = TRANSLATIONS[lang] || TRANSLATIONS.fr;
  let str = bag[key] ?? TRANSLATIONS.fr[key] ?? key;
  if (params && typeof str === "string") {
    Object.keys(params).forEach((k) => {
      str = str.replace(new RegExp(`\\{${k}\\}`, "g"), String(params[k]));
    });
  }
  return str;
}

export default TRANSLATIONS;
